import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Zap, DollarSign, Hash, Activity, Filter, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Label, EmptyState } from "../../../components/common";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { fmtDateTime } from "../../../lib/time";

const PERIODS = [{v:7,label:"7d"},{v:30,label:"30d"},{v:90,label:"90d"}];

export default function PlatformAiUsage() {
  const [days, setDays] = useState(30);
  const [feature, setFeature] = useState("all");
  const [orgId, setOrgId] = useState("all");
  const [locationId, setLocationId] = useState("all");
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [locBreakdown, setLocBreakdown] = useState([]);
  const [search, setSearch] = useState("");

  const qs = useCallback(() => {
    const p = new URLSearchParams({ days: String(days) });
    if (feature !== "all") p.set("feature", feature);
    if (orgId !== "all") p.set("org_id", orgId);
    if (locationId !== "all") p.set("location_id", locationId);
    return p.toString();
  }, [days, feature, orgId, locationId]);

  const load = useCallback(async () => {
    try {
      const [s, c, lb] = await Promise.all([
        api.get(`/platform/ai-usage/summary?${qs()}`),
        api.get(`/platform/ai-usage/calls?${qs()}&limit=200`),
        orgId !== "all" ? api.get(`/platform/ai-usage/by-location?${qs()}`) : Promise.resolve({ data: [] }),
      ]);
      setSummary(s.data); setCalls(c.data); setLocBreakdown(lb.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [qs, orgId]);

  useEffect(() => { load(); }, [load]);

  const filteredCalls = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return calls;
    return calls.filter((c) => [c.org_name, c.feature, c.model, c.location_id, c.error]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(t)));
  }, [calls, search]);

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/platform/ai-usage/export.csv?${qs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ai-usage-${days}d.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!summary) return <div className="text-slate-400">Loading AI usage…</div>;
  const t = summary.totals;

  return (
    <div className="space-y-6" data-testid="platform-ai-usage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>Servantage · Platform</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">AI Credit Usage</h1>
          <p className="mt-1 text-sm text-slate-500">Every Gemini call across every tenant with tokens + cost estimate.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white p-1" data-testid="ai-usage-period">
            {PERIODS.map((p) => (
              <button key={p.v} onClick={() => setDays(p.v)} data-testid={`ai-period-${p.v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${days === p.v ? "bg-[#4338CA] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={downloadCsv} data-testid="ai-usage-csv">
            <Download className="mr-1 h-4 w-4" /> CSV
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select value={orgId} onValueChange={(v) => { setOrgId(v); setLocationId("all"); }}>
          <SelectTrigger className="w-56" data-testid="filter-org"><SelectValue placeholder="All organisations" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organisations</SelectItem>
            {summary.by_org.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.org_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={feature} onValueChange={setFeature}>
          <SelectTrigger className="w-48" data-testid="filter-feature"><SelectValue placeholder="All features" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All features</SelectItem>
            {summary.by_feature.map((f) => <SelectItem key={f.feature} value={f.feature}>{f.feature}</SelectItem>)}
          </SelectContent>
        </Select>
        {orgId !== "all" && locBreakdown.length > 0 && (
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-56" data-testid="filter-location"><SelectValue placeholder="All locations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locBreakdown.filter(l => l.location_id).map((l) => (
                <SelectItem key={l.location_id} value={l.location_id}>{l.location_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative ml-auto min-w-[220px] flex-1 max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search calls" className="pl-9" />
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Calls" value={t.calls.toLocaleString()} icon={Zap} testId="kpi-calls" />
        <Kpi label="Total tokens" value={t.total_tokens.toLocaleString()} icon={Hash} testId="kpi-tokens"
             sub={`${t.input_tokens.toLocaleString()} in · ${t.output_tokens.toLocaleString()} out`} />
        <Kpi label="Estimated cost (ZAR)" value={`R${t.cost_zar.toFixed(2)}`} icon={DollarSign} testId="kpi-cost" accent="#4338CA" />
        <Kpi label="Errors" value={t.errors} icon={Activity} testId="kpi-errors"
             accent={t.errors > 0 ? "#DC2626" : "#10B981"} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="By organisation" subtitle="Click a row to filter the whole view">
          {summary.by_org.length === 0 ? <EmptyState icon={Zap} title="No usage yet" /> : (
            <ol className="space-y-1" data-testid="by-org-list">
              {summary.by_org.map((o) => (
                <li key={o.org_id || "none"}>
                  <button onClick={() => setOrgId(o.org_id)} data-testid={`org-row-${o.org_id || "none"}`}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-indigo-50">
                    <span className="truncate font-semibold text-slate-800">{o.org_name}</span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {o.calls} calls · {o.total_tokens.toLocaleString()} tok · <b className="text-slate-800">R{o.cost_zar.toFixed(2)}</b>
                      <ChevronRight className="ml-1 inline h-3 w-3" />
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Panel>
        <Panel title="By feature">
          {summary.by_feature.length === 0 ? <EmptyState icon={Zap} title="No usage yet" /> : (
            <ol className="space-y-1" data-testid="by-feature-list">
              {summary.by_feature.map((f) => (
                <li key={f.feature}>
                  <button onClick={() => setFeature(f.feature)} data-testid={`feature-row-${f.feature}`}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-indigo-50">
                    <span className="truncate font-semibold text-slate-800">{f.feature}</span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {f.calls} calls · {f.total_tokens.toLocaleString()} tok · <b className="text-slate-800">R{f.cost_zar.toFixed(2)}</b>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Panel>
      </div>

      {orgId !== "all" && locBreakdown.length > 0 && (
        <Panel title="By location" subtitle={`Across all AI usage for the selected org in the last ${days} days`}>
          <ol className="space-y-1" data-testid="by-location-list">
            {locBreakdown.map((l) => (
              <li key={l.location_id || "none"} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm">
                <span className="truncate font-semibold text-slate-800">{l.location_name}</span>
                <span className="shrink-0 text-xs text-slate-500">
                  {l.calls} calls · {l.total_tokens.toLocaleString()} tok · <b className="text-slate-800">R{l.cost_zar.toFixed(2)}</b>
                </span>
              </li>
            ))}
          </ol>
        </Panel>
      )}

      <Panel title="Recent calls" subtitle="Latest 200 AI calls in the window">
        {filteredCalls.length === 0 ? <EmptyState icon={Zap} title="No calls" /> : (
          <div className="overflow-x-auto sv-scroll">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Org</th>
                    <th className="px-3 py-2">Feature</th><th className="px-3 py-2">Model</th>
                    <th className="px-3 py-2 text-right">In</th><th className="px-3 py-2 text-right">Out</th>
                    <th className="px-3 py-2 text-right">ZAR</th><th className="px-3 py-2 text-right">ms</th>
                    <th className="px-3 py-2">Status</th></tr>
              </thead>
              <tbody data-testid="calls-table">
                {filteredCalls.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-500">{fmtDateTime(c.created_at)}</td>
                    <td className="px-3 py-2 font-semibold text-slate-800">{c.org_name}</td>
                    <td className="px-3 py-2"><span className="rounded bg-indigo-50 px-1.5 py-0.5 text-xs font-semibold text-indigo-700">{c.feature}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.model}</td>
                    <td className="px-3 py-2 text-right">{c.input_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{c.output_tokens.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold">R{c.cost_zar.toFixed(4)}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{c.duration_ms}</td>
                    <td className="px-3 py-2">
                      {c.status === "ok" ? <span className="text-emerald-600">ok</span>
                        : <span title={c.error} className="text-red-600">error</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, testId, accent = "#0F172A" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
        <Icon className="h-4 w-4 text-slate-300" />
      </div>
      <p className="mt-2 sv-metric text-2xl font-bold" style={{ color: accent }}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3">
        <p className="font-head text-sm font-bold text-slate-900">{title}</p>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
